import { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { getMonthName, getDayName } from "../../Utils/Constants";
import { ICalendarProps, ICalendarEvent } from "../../Utils/Types";
import styles from "./Calendar.module.css";

/**
 * Reusable Calendar component for displaying dates with events
 * Features:
 * - Romanian localization using Intl API
 * - Customizable event rendering
 * - Month navigation
 * - Date selection with callbacks
 * - Responsive design with Material-UI
 */
export default function Calendar({
  events = [],
  onDateSelect,
  onMonthChange,
  initialDate = new Date(),
  renderEventIndicator,
  className,
  showNavigation = true,
  locale = 'ro-RO'
}: ICalendarProps) {
  // Current displayed month/year
  const [currentDate, setCurrentDate] = useState(initialDate);
  // Currently selected day number
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // Generate Romanian day names starting from Monday (index 1)
  // Array maps: [Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday]
  const dayNames = [1, 2, 3, 4, 5, 6, 0].map(dayIndex => getDayName(dayIndex));

  /**
   * Navigate between months and trigger callback
   * @param direction - 'prev' for previous month, 'next' for next month
   */
  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(currentDate.getMonth() - 1);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
    setSelectedDay(null); // Reset selection when changing months
    onMonthChange?.(newDate);
  };

  /**
   * Generate array of days for the current month
   * Includes null values for empty cells at the beginning
   * @returns Array of day numbers and null values
   */
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Adjust for Monday start

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  /**
   * Get events that occur on a specific day
   * @param day - Day number (1-31)
   * @returns Array of events for that day
   */
  const getEventsForDay = (day: number): ICalendarEvent[] => {
    if (!day) return [];
    // Format: YYYY-MM-DD
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => event.date.startsWith(dateStr));
  };

  /**
   * Handle clicking on a calendar day
   * @param day - Day number or null for empty cells
   */
  const handleDayClick = (day: number | null) => {
    if (!day) return;
    
    const dayEvents = getEventsForDay(day);
    setSelectedDay(day);
    
    // Create full Date object for the selected day
    const selectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    onDateSelect?.(selectedDate, dayEvents);
  };

  /**
   * Render individual calendar day cell
   * @param day - Day number or null for empty cell
   * @param index - Array index for React key
   * @returns JSX for the calendar day
   */
  const renderCalendarDay = (day: number | null, index: number) => {
    if (!day) {
      return <div key={index} className={styles.emptyDay} />;
    }

    const dayEvents = getEventsForDay(day);
    const hasEvents = dayEvents.length > 0;
    const hasEnrolledEvents = dayEvents.some(event => event.isClientEnrolled);
    const canEnrollInAnyEvent = dayEvents.some(event => event.canEnroll && !event.isClientEnrolled);
    const isSelected = selectedDay === day;
    const isDisabled = !hasEvents || (hasEvents && !canEnrollInAnyEvent && !hasEnrolledEvents);

    return (
      <div 
        key={index} 
        className={`${styles.calendarDay} ${hasEvents ? styles.hasEvents : ''} ${hasEnrolledEvents ? styles.hasEnrolledEvents : ''} ${isDisabled ? styles.disabledDay : ''} ${isSelected ? styles.selectedDay : ''}`}
        onClick={() => !isDisabled && handleDayClick(day)}
        style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
      >
        <Typography variant="body2" className={styles.dayNumber}>
          {day}
        </Typography>
        {hasEnrolledEvents && (
          <Typography 
            variant="caption" 
            className={styles.enrolledText}
            sx={{ 
              fontSize: '9px',
              color: '#4caf50',
              fontWeight: 600,
              textAlign: 'center',
              lineHeight: 1
            }}
          >
            Înscris
          </Typography>
        )}
        {hasEvents && (
          <div className={styles.eventIndicator}>
            {renderEventIndicator ? (
              renderEventIndicator(dayEvents)
            ) : (
              <Typography variant="caption" className={styles.eventCount}>
                {dayEvents.length}
              </Typography>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className={`${styles.calendarCard} ${className || ''}`}>
      <CardContent>
        {/* Calendar Header - Month navigation */}
        {showNavigation && (
          <Box className={styles.calendarHeader}>
            <IconButton 
              onClick={() => navigateMonth('prev')}
              className={styles.navButton}
              aria-label="Previous month"
            >
              <ChevronLeft />
            </IconButton>
            
            <Typography variant="h5" className={styles.monthYear}>
              {getMonthName(currentDate.getMonth())} {currentDate.getFullYear()}
            </Typography>
            
            <IconButton 
              onClick={() => navigateMonth('next')}
              className={styles.navButton}
              aria-label="Next month"
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}

        {/* Day names header - Mon, Tue, Wed, etc. */}
        <Box className={styles.dayNamesHeader}>
          {dayNames.map((dayName, index) => (
            <Typography key={index} variant="caption" className={styles.dayName}>
              {dayName}
            </Typography>
          ))}
        </Box>
        
        {/* Calendar grid - 7x6 grid of days */}
        <Box className={styles.calendarDaysGrid}>
          {getDaysInMonth().map((day, index) => renderCalendarDay(day, index))}
        </Box>
      </CardContent>
    </Card>
  );
}